import { useState, useEffect } from 'react';
import * as React from 'react';
import gettext from '@sdk/lang/gettext';
import EditablePicker from '@sdk/components/forms/EditablePicker';
import Column from '@sdk/components/ui/spacing/Column';
import Text14 from '@sdk/components/ui/typography/Text14';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import validateNumber, {
  ValidateOptions,
  ValidNumberOption,
} from '@sdk/utils/validation/validateNumber';
import { TIMECLOCK_NOTIFICATIONS } from '@sdk/common/constants';

import CollapsibleSettingsSection from '../CollapsibleSettingsSection';
import RowSpaceBetween from '../RowSpaceBetween';
import SettingsSwitcherRow from '../SettingsSwitcherRow';
import { TabType } from '../../types';
import { useSettingsErrorsContext } from '../../SettingsErrorContext';
import {
  CLOCK_NOTIFICATIONS_DEFAULT_VALUE,
  CLOCK_NOTIFICATIONS_MINUTES_OPTIONS,
  FIELD_WIDTH_205,
} from '../../constants';
import { useUserSettings } from '../../UserSettingsContext';

const TimeclockNotificationEmployeeSettings: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { userSettings, onUserSettingsChange } = useUserSettings();
  const { missingClockInNotificationsMinutes, missingClockOutNotificationsMinutes } = userSettings;
  const isNotificationEnabled = (value: number): boolean => {
    // notification has -1 value when is disabled for Employee
    return value !== -1;
  };
  const { toggleError } = useSettingsErrorsContext();
  const [clockInNotificationEnabled, setClockInNotificationEnabled] = useState(
    isNotificationEnabled(missingClockInNotificationsMinutes)
  );
  const [clockOutNotificationEnabled, setClockOutNotificationEnabled] = useState(
    isNotificationEnabled(missingClockOutNotificationsMinutes)
  );

  useEffect(() => {
    setClockInNotificationEnabled(isNotificationEnabled(missingClockInNotificationsMinutes));
    setClockOutNotificationEnabled(isNotificationEnabled(missingClockOutNotificationsMinutes));
  }, [missingClockInNotificationsMinutes, missingClockOutNotificationsMinutes]);

  const clockInNotificationChangeHandler = (value: boolean): void => {
    setClockInNotificationEnabled(value);
    toggleError(TabType.notifications, 'missingClockInNotificationsMinutes', false);

    if (value) {
      onUserSettingsChange({
        missingClockInNotificationsMinutes: CLOCK_NOTIFICATIONS_DEFAULT_VALUE,
      });
      return;
    }

    onUserSettingsChange({
      missingClockInNotificationsMinutes: -1,
      clockInNotifications: TIMECLOCK_NOTIFICATIONS.NEVER,
    });
  };

  const clockOutNotificationChangeHandler = (value: boolean): void => {
    setClockOutNotificationEnabled(value);
    toggleError(TabType.notifications, 'missingClockOutNotificationsMinutes', false);

    if (value) {
      onUserSettingsChange({
        missingClockOutNotificationsMinutes: CLOCK_NOTIFICATIONS_DEFAULT_VALUE,
      });
      return;
    }

    onUserSettingsChange({
      missingClockOutNotificationsMinutes: -1,
      clockOutNotifications: TIMECLOCK_NOTIFICATIONS.NEVER,
    });
  };

  return (
    <>
      <CollapsibleSettingsSection
        pBottom={20}
        isActive={isNotificationEnabled(missingClockInNotificationsMinutes)}
        headerItem={
          <SettingsSwitcherRow
            value={isNotificationEnabled(missingClockInNotificationsMinutes)}
            subtitle={gettext('Enable notifications when I forget to clock in to my shifts')}
            onChange={clockInNotificationChangeHandler}
          />
        }
        contentItem={
          <Column pTop={20}>
            <RowSpaceBetween>
              <Text14 themeColor={ThemeColorType.gray}>
                {gettext('How many minutes after the shift is scheduled to start')}
              </Text14>
              <EditablePicker
                options={CLOCK_NOTIFICATIONS_MINUTES_OPTIONS}
                value={missingClockInNotificationsMinutes}
                postfix="Minutes"
                onChange={(value: number): void => {
                  onUserSettingsChange({ missingClockInNotificationsMinutes: Number(value) });
                  toggleError(
                    TabType.notifications,
                    'missingClockInNotificationsMinutes',
                    !validateNumber(value, {
                      [ValidateOptions.validNumber]: clockInNotificationEnabled
                        ? ValidNumberOption.nonNegative
                        : ValidNumberOption.negative,
                      [ValidateOptions.validNumberGreaterOrEqualThan]:
                        CLOCK_NOTIFICATIONS_DEFAULT_VALUE,
                      [ValidateOptions.integer]: true,
                    })
                  );
                }}
                customWidth={FIELD_WIDTH_205}
                usePickerValueWhenDisplay
                invalid={
                  !validateNumber(missingClockInNotificationsMinutes, {
                    [ValidateOptions.validNumber]: clockInNotificationEnabled
                      ? ValidNumberOption.nonNegative
                      : ValidNumberOption.negative,
                    [ValidateOptions.validNumberGreaterOrEqualThan]:
                      CLOCK_NOTIFICATIONS_DEFAULT_VALUE,
                    [ValidateOptions.integer]: true,
                  })
                }
              />
            </RowSpaceBetween>
          </Column>
        }
      />
      <CollapsibleSettingsSection
        isActive={isNotificationEnabled(missingClockOutNotificationsMinutes)}
        headerItem={
          <SettingsSwitcherRow
            value={isNotificationEnabled(missingClockOutNotificationsMinutes)}
            subtitle={gettext('Enable notifications when I forget to clock out of my shifts')}
            onChange={clockOutNotificationChangeHandler}
          />
        }
        contentItem={
          <Column pTop={20}>
            <RowSpaceBetween>
              <Text14 themeColor={ThemeColorType.gray}>
                {gettext('How many minutes after the shift is scheduled to end')}
              </Text14>
              <EditablePicker
                options={CLOCK_NOTIFICATIONS_MINUTES_OPTIONS}
                value={missingClockOutNotificationsMinutes}
                postfix="Minutes"
                onChange={(value: number): void => {
                  onUserSettingsChange({ missingClockOutNotificationsMinutes: Number(value) });
                  toggleError(
                    TabType.notifications,
                    'missingClockOutNotificationsMinutes',
                    !validateNumber(value, {
                      [ValidateOptions.validNumber]: clockOutNotificationEnabled
                        ? ValidNumberOption.nonNegative
                        : ValidNumberOption.negative,
                      [ValidateOptions.validNumberGreaterOrEqualThan]:
                        CLOCK_NOTIFICATIONS_DEFAULT_VALUE,
                      [ValidateOptions.integer]: true,
                    })
                  );
                }}
                customWidth={FIELD_WIDTH_205}
                usePickerValueWhenDisplay
                invalid={
                  !validateNumber(missingClockOutNotificationsMinutes, {
                    [ValidateOptions.validNumber]: clockOutNotificationEnabled
                      ? ValidNumberOption.nonNegative
                      : ValidNumberOption.negative,
                    [ValidateOptions.validNumberGreaterOrEqualThan]:
                      CLOCK_NOTIFICATIONS_DEFAULT_VALUE,
                    [ValidateOptions.integer]: true,
                  })
                }
              />
            </RowSpaceBetween>
          </Column>
        }
      />
    </>
  );
};

export default TimeclockNotificationEmployeeSettings;
